import type { JsonObject } from "@/api";
import { asLeaf, deepEqualLeaf, getAtPath, isInterpolationString } from "./recipeCoerce";
import type { OverrideOp, ParsedOverrideKey, RecipeEdit } from "./recipeTypes";

// Hydra dot-notation override 계산. override 는 flat dict 이고 +/++/~ 접두사는 "키 문자열 안"에 있다
// (ConfigReq.overrides 형식과 같다). base 위에 override 를 적용한 effective 트리도 여기서 계산한다.

// 키 접두사를 op 로 분리한다. ++ 를 + 보다 먼저 확인한다.
//
// 이 함수는 접두사 해석의 단일 소유자다. 소비처가 `replace(/^[+~]+/, "")` 같은 사본을 두면
// delete(`~`)를 set 으로 잘못 판정한다. delete 는 "값을 바꾼다"가 아니라 "키를 지운다"는 뜻이다.
// 그래서 판정하는 쪽은 path 와 op 를 함께 읽어야 한다(예약 키 검사처럼 op 를 구분하지 않는 쪽은 path 만 읽는다).
export function parseOverrideKey(key: string): ParsedOverrideKey {
  if (key.startsWith("++")) {
    return { op: "force", path: key.slice(2) };
  }
  if (key.startsWith("+")) {
    return { op: "add", path: key.slice(1) };
  }
  if (key.startsWith("~")) {
    return { op: "delete", path: key.slice(1) };
  }
  return { op: "set", path: key };
}

export function formatOverrideKey(op: OverrideOp, path: string): string {
  switch (op) {
    case "set":
      return path;
    case "add":
      return `+${path}`;
    case "force":
      return `++${path}`;
    case "delete":
      return `~${path}`;
  }
}

// 구조화 편집 → 최소 override dict. 변경된 leaf 만 방출한다(서브트리 덤프 없음). base 에 경로가 있으면
// plain override 를 쓴다(OmegaConf 가 base 스키마로 타입을 검사한다). 없으면 ++(add-or-override, 멱등)를
// 쓴다. base 값이 인터폴레이션이면 건너뛴다(${...} 를 override 로 전달하지 않는다).
export function diffToOverrides(base: JsonObject, edits: readonly RecipeEdit[]): JsonObject {
  const overrides: JsonObject = {};
  for (const edit of edits) {
    const baseValue = getAtPath(base, edit.path);
    if (isInterpolationString(baseValue)) {
      continue;
    }
    if (deepEqualLeaf(edit.value, asLeaf(baseValue))) {
      continue;
    }
    const present = baseValue !== undefined;
    const op: OverrideOp = present ? "set" : "force";
    overrides[formatOverrideKey(op, edit.path)] = edit.value;
  }
  return overrides;
}

// override 레이어들을 왼쪽→오른쪽으로 합친다(뒤 레이어가 우선한다). JsonObject 는 키 순서·중복을
// 담을 수 없으므로 접두사를 뗀 canonical 경로로 정규화한다(경로당 하나). 같은 경로에서 뒤 레이어의 ~ 가
// 앞 레이어의 set 을 대체한다.
export function mergeOverrides(...layers: JsonObject[]): JsonObject {
  const byPath = new Map<string, { op: OverrideOp; value: unknown }>();
  for (const layer of layers) {
    for (const [key, value] of Object.entries(layer)) {
      const { op, path } = parseOverrideKey(key);
      byPath.set(path, op === "delete" ? { op: "delete", value: null } : { op, value });
    }
  }
  const merged: JsonObject = {};
  for (const [path, entry] of byPath) {
    merged[formatOverrideKey(entry.op, path)] = entry.value;
  }
  return merged;
}

// base 트리에 override 를 적용한 effective 트리를 만든다(base 는 변형하지 않는다). set/add/force 는
// 값을 쓰고, delete(~)는 경로를 지운다. 인터폴레이션은 여전히 문자열로 남으므로 완전 해석은 아니다.
export function applyOverridesToData(base: JsonObject, overrides: JsonObject): JsonObject {
  const result = structuredClone(base);
  for (const [key, value] of Object.entries(overrides)) {
    const { op, path } = parseOverrideKey(key);
    if (op === "delete") {
      deleteAtPath(result, path);
    } else {
      setAtPath(result, path, value);
    }
  }
  return result;
}

function setAtPath(target: JsonObject, path: string, value: unknown): void {
  const keys = path.split(".");
  let cursor: Record<string, unknown> = target;
  for (let index = 0; index < keys.length - 1; index += 1) {
    const key = keys[index];
    const next = cursor[key];
    if (next === null || typeof next !== "object" || Array.isArray(next)) {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  }
  cursor[keys[keys.length - 1]] = value;
}

function deleteAtPath(target: JsonObject, path: string): void {
  const keys = path.split(".");
  let cursor: unknown = target;
  for (let index = 0; index < keys.length - 1; index += 1) {
    const key = keys[index];
    if (Array.isArray(cursor)) {
      const position = toArrayIndex(key);
      if (position === null) {
        return;
      }
      cursor = cursor[position];
      continue;
    }
    if (cursor !== null && typeof cursor === "object") {
      cursor = (cursor as Record<string, unknown>)[key];
      continue;
    }
    return;
  }
  const lastKey = keys[keys.length - 1];
  if (Array.isArray(cursor)) {
    const position = toArrayIndex(lastKey);
    if (position !== null) {
      cursor.splice(position, 1);
    }
    return;
  }
  if (cursor !== null && typeof cursor === "object") {
    delete (cursor as Record<string, unknown>)[lastKey];
  }
}

function toArrayIndex(segment: string): number | null {
  if (/^\d+$/.test(segment)) {
    return Number.parseInt(segment, 10);
  }
  return null;
}
